import { useToast } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import api from "@/common/utils/api";
import { translateError } from "@/common/utils/errorHandling";
import { feedbackQueryKeys } from "@/feedback/data/feedbackQueryKeys";
import { FeedbackPushResultDTO } from "@reflag/shared/feedbackAPI";
import {
  NotificationProvider,
  NotificationProviderTitle,
} from "@reflag/shared/notification";

export function useFeedbackPushMutation(
  provider: NotificationProvider,
  feedbackId?: string,
  onPushed?: (data: FeedbackPushResultDTO) => void,
) {
  const { appId, envId } = useEnvironment();
  const toast = useToast();
  const errorToast = useErrorToast();

  const providerTitle = NotificationProviderTitle[provider];

  return useMutation({
    mutationKey: feedbackQueryKeys.push(appId, envId, provider, feedbackId),
    mutationFn: () =>
      api
        .post<"/apps/:appId/feedback/:feedbackId/push/:provider">(
          `/apps/${appId}/feedback/${feedbackId}/push/${provider}`,
          null,
          {
            params: { envId: envId! },
          },
        )
        .then((res) => res.data),

    onSuccess: (data) => {
      if (!onPushed) {
        toast({
          title: `Feedback pushed to ${providerTitle}`,
        });
      } else {
        onPushed(data);
      }
    },
    onError: (error) => {
      const { title, description } = translateError(
        error,
        `Failed to push feedback to ${providerTitle}`,
      );

      errorToast({
        title,
        description,
        duration: 5000,
      });
    },
  });
}
